import {
  EvaluationCriteriaWithScores,
  KpiInput,
  ProviderKpisInput,
  ProviderTotalInput,
} from '@frontend/shared-api';
import {
  companyKpis,
  financeKpis,
  providerRawAnswers,
  RawAnswers,
} from '../../rfi-individual-results-modal/rfi-individual-results.calc';
import {ProviderResult} from './provider-results-table.types';

type KpiValue = number | boolean | null;

interface KpiEntry {
  name: string;
  value: KpiValue;
  unit: string;
}

export function buildAiSummaryTotals(
  providerIds: string[],
  maxValue: number,
  totalsByProviderId: Map<string, ProviderResult>,
): ProviderTotalInput[] {
  const scores = providerIds.flatMap((providerId) => {
    const total = totalsByProviderId.get(providerId);
    return total ? [{providerId, score: total.value}] : [];
  });
  const sortedScores = scores.map((entry) => entry.score).sort((first, second) => second - first);

  return scores.map((entry) => ({
    providerId: entry.providerId,
    score: entry.score,
    maxScore: maxValue,
    rank: sortedScores.indexOf(entry.score) + 1,
  }));
}

export function buildAiSummaryKpis(
  criteria: EvaluationCriteriaWithScores[],
  providerIds: string[],
): ProviderKpisInput[] {
  return providerIds
    .map((providerId) => ({
      providerId,
      values: collectKpis(providerRawAnswers(criteria, providerId)),
    }))
    .filter((providerKpis) => providerKpis.values.length > 0);
}

function collectKpis(answers: RawAnswers): KpiInput[] {
  const finance = financeKpis(answers);
  const company = companyKpis(answers);
  const entries: KpiEntry[] = [
    {name: 'equity ratio', value: finance.equityRatio, unit: '%'},
    {name: 'research and development ratio', value: finance.researchRatio, unit: '%'},
    {name: 'years on the market', value: finance.marketYears, unit: ''},
    {name: 'liability insurance', value: finance.liabilityInsurance, unit: ''},
    {name: 'recurring revenue share', value: finance.recurringRevenueShare, unit: '%'},
    {name: 'revenue share of the largest customer', value: finance.largestCustomerShare, unit: '%'},
    {name: 'annual revenue growth', value: finance.revenueGrowth, unit: '%'},
    {name: 'EBITDA margin', value: finance.ebitdaMargin, unit: '%'},
    {name: 'cloud revenue share', value: finance.cloudRevenueShare, unit: '%'},
    {name: 'revenue of the latest year', value: finance.years.at(-1)?.revenue ?? null, unit: ''},
    {name: 'active customers', value: company.activeCustomers, unit: ''},
    {name: 'gross revenue retention', value: company.grossRevenueRetention, unit: '%'},
    {name: 'employees in HR cloud', value: company.hrCloudEmployees, unit: ''},
    {name: 'annual employee growth', value: company.employeeGrowth, unit: '%'},
    {name: 'implementation partners', value: company.implementationPartners, unit: ''},
    {name: 'technology partners', value: company.technologyPartners, unit: ''},
    {name: 'ISO 27001 certified', value: company.iso27001, unit: ''},
    {name: 'data centers in the EEA', value: company.dataCentersInEea, unit: ''},
    {name: 'employees in development', value: company.development, unit: ''},
    {name: 'employees in operations', value: company.operations, unit: ''},
    {name: 'employees in customer support', value: company.customerSupport, unit: ''},
  ];

  return entries.flatMap((entry) =>
    entry.value === null
      ? []
      : [{name: entry.name, value: formatKpiValue(entry.value, entry.unit)}],
  );
}

function formatKpiValue(value: number | boolean, unit: string): string {
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }
  return `${Math.round(value * 10) / 10}${unit}`;
}
